import React, { useCallback, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import Fade from "../utils/Fade";
import { useUser } from "../contexts/UserContext";
import api from "../api";
import PrefillConsentCard from "../components/PrefillConsentCard";
import { emailState } from "../utils/emailState";
import DeleteAccountDialog from "../components/DeleteAccountDialog";

export default function AlphaThankYouPage() {
  const { user, setUser, loading: userLoading } = useUser();
  const [email, setEmail] = useState(user?.email || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // "Use a different address": show the form again over a pending one.
  const [editing, setEditing] = useState(false);
  const [resent, setResent] = useState(false);
  // One request at a time: each POST mails a link that voids the one before.
  const inFlight = useRef(false);

  // The address binds only when the link we send is confirmed (#260).
  // Until then it is pending, and this page has to keep a way forward:
  // the link expires, mail gets lost, addresses get mistyped, and a
  // waitlisted user can reach no other page to fix it.
  const hasEmail = user && user.email && user.email.trim() !== "";
  const pending = user && user.pending_email;
  const showForm = user && (editing || (!hasEmail && !pending));
  const showPending = pending && !editing;

  // This page is handed out as a link (e.g. to fresh X signups, so they can
  // opt in to the tweet seed). Signed-up users are logged in even before
  // approval, so they land here directly. Anyone without a session is a
  // stranger — send them to the landing page (a logged-out signup who logs
  // in from there is bounced back here by ProtectedRoute anyway). Can't use
  // ProtectedRoute on this page itself: it redirects unapproved users
  // *here*, which would loop.
  if (userLoading) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <p>Loading...</p>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/landing" replace />;
  }

  const sendLink = async (address, { isResend = false } = {}) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError("");
    setResent(false);
    try {
      const response = await api.post("/dashboard/email", { email: address });
      setUser((prev) => ({ ...prev, ...emailState(response.data) }));
      setEditing(false);
      setResent(isResend);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Error sending the confirmation link. Please try again.");
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendLink(email);
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "4rem 2rem", textAlign: "center",
      position: "relative",
    }}>
      {/* Warm glow */}
      <div style={{
        position: "absolute", top: "30%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: 500, height: 500, borderRadius: "50%",
        background: "radial-gradient(circle, var(--accent-glow) 0%, transparent 65%)",
        pointerEvents: "none", opacity: 0.4,
      }} />

      <Fade>
        <img
          src={process.env.PUBLIC_URL + "/loore-logo-transparent.svg"}
          alt="Loore"
          style={{
            width: "2.5rem", height: "2.5rem",
            marginBottom: "2rem", opacity: 0.6,
          }}
        />
      </Fade>
      <Fade delay={0.1}>
        <h1 style={{
          fontFamily: "var(--serif)", fontWeight: 300,
          fontSize: "clamp(2rem, 5vw, 3.2rem)", lineHeight: 1.2,
          color: "var(--text-primary)", maxWidth: 600, marginBottom: "1.5rem",
        }}>
          You're part of this now.
        </h1>
      </Fade>
      <Fade delay={0.2}>
        <p style={{
          fontFamily: "var(--sans)", fontWeight: 300, fontSize: "1.05rem",
          lineHeight: 1.8, color: "var(--text-secondary)", maxWidth: 480,
          marginBottom: "3rem",
        }}>
          Thank you for signing up for the Loore Alpha. We're letting people in
          gradually — to keep things intimate and to give each person the attention
          they deserve as they begin.
        </p>
      </Fade>

      {/* Email form for users without email */}
      {showForm && (
        <Fade delay={0.25}>
          <div style={{
            background: "var(--bg-card)", border: "1px solid var(--border)",
            borderRadius: 12, padding: "2rem 2.2rem", maxWidth: 460,
            textAlign: "left", marginBottom: "2rem",
          }}>
            <div style={{
              fontFamily: "var(--sans)", fontSize: "0.7rem", letterSpacing: "0.15em",
              textTransform: "uppercase", color: "var(--accent)", opacity: 0.7,
              marginBottom: "1rem",
            }}>Get notified</div>
            <p style={{
              fontFamily: "var(--sans)", fontWeight: 300, fontSize: "0.92rem",
              lineHeight: 1.7, color: "var(--text-secondary)", marginBottom: "1.2rem",
            }}>
              Leave your email and we'll let you know when your spot opens up.
            </p>
            <form onSubmit={handleSubmit}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  backgroundColor: "var(--bg-deep)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--border)",
                  borderRadius: "8px",
                  fontFamily: "var(--sans)",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  marginBottom: "1rem",
                }}
              />
              {error && <div style={{ color: "var(--accent)", marginBottom: "0.8rem", fontSize: "0.88rem" }}>{error}</div>}
              <button
                type="submit"
                disabled={loading}
                style={{
                  border: "1px solid var(--accent)",
                  color: "var(--accent)",
                  background: "transparent",
                  padding: "10px 24px",
                  borderRadius: "6px",
                  fontFamily: "var(--sans)",
                  fontSize: "0.88rem",
                  fontWeight: 400,
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
              {editing && (
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => { setError(""); setEditing(false); }}
                  style={{ ...pendingActionStyle(loading), marginLeft: "1.2rem" }}
                >
                  Keep {user.pending_email}
                </button>
              )}
            </form>
          </div>
        </Fade>
      )}

      {/* Pending: the address binds when the link is confirmed (#260). */}
      {showPending && (
        <Fade delay={0.25}>
          <div style={{
            background: "var(--bg-card)", border: "1px solid var(--border)",
            borderRadius: 12, padding: "1.4rem 2.2rem", maxWidth: 460,
            textAlign: "left", marginBottom: "2rem",
            fontFamily: "var(--sans)", fontWeight: 300, fontSize: "0.92rem",
            lineHeight: 1.7, color: "var(--text-secondary)",
            overflowWrap: "anywhere",
          }}>
            {user.pending_email_expired ? (
              <>
                The confirmation link we sent to{" "}
                <strong style={{ color: "var(--text-primary)", fontWeight: 400 }}>{user.pending_email}</strong>{" "}
                has expired. Until the address is confirmed we have no way to reach you.
              </>
            ) : (
              <>
                We sent a confirmation link to{" "}
                <strong style={{ color: "var(--text-primary)", fontWeight: 400 }}>{user.pending_email}</strong>.
                Open it to finish — until then we have no way to reach you.
              </>
            )}
            <div style={{ marginTop: "0.6rem", color: "var(--text-muted)" }}>
              {resent ? "New link sent. The earlier one no longer works. " : ""}
              {PENDING_HINT}
            </div>
            {error && <div style={{ color: "var(--accent)", marginTop: "0.6rem", fontSize: "0.88rem" }}>{error}</div>}
            <div style={{ marginTop: "0.9rem", display: "flex", gap: "1.2rem", flexWrap: "wrap" }}>
              <button
                type="button"
                disabled={loading}
                onClick={() => sendLink(user.pending_email, { isResend: true })}
                style={pendingActionStyle(loading)}
              >
                {loading ? "Sending..." : (user.pending_email_expired ? "Send a new link" : "Send it again")}
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={() => { setEmail(user.pending_email); setError(""); setResent(false); setEditing(true); }}
                style={pendingActionStyle(loading)}
              >
                Use a different address
              </button>
            </div>
          </div>
        </Fade>
      )}
      {/* Opt-in: seed the account from the user's own public tweets. Asked
          here because it's dead time — the signup is already done, so a
          hesitation costs nothing. Renders null unless X-login + unanswered. */}
      <Fade delay={0.26}>
        <PrefillConsentCard style={{ marginBottom: "2rem" }} />
      </Fade>

      <Fade delay={0.28}>
        <div style={{
          background: "var(--bg-card)", border: "1px solid var(--border)",
          borderRadius: 12, padding: "2rem 2.2rem", maxWidth: 460,
          textAlign: "left", marginBottom: "3rem",
        }}>
          <div style={{
            fontFamily: "var(--sans)", fontSize: "0.7rem", letterSpacing: "0.15em",
            textTransform: "uppercase", color: "var(--accent)", opacity: 0.7,
            marginBottom: "1.2rem",
          }}>What happens next</div>

          <div style={{ display: "flex", flexDirection: "column", gap: "1.2rem" }}>
            {[
              { num: "01", text: "We'll send you an email when your spot opens up. It shouldn't be long." },
              { num: "02", text: "You'll get a warm welcome with everything you need to begin." },
              { num: "03", text: "Start journaling — by text or voice — and let your lore unfold." },
            ].map(({ num, text }) => (
              <div key={num} style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>
                <span style={{
                  fontFamily: "var(--serif)", fontSize: "1.1rem", fontWeight: 300,
                  color: "var(--accent)", opacity: 0.5, flexShrink: 0, marginTop: 2,
                }}>{num}</span>
                <p style={{
                  fontFamily: "var(--sans)", fontWeight: 300, fontSize: "0.92rem",
                  lineHeight: 1.7, color: "var(--text-secondary)",
                }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </Fade>

      <Fade delay={0.36}>
        <p style={{
          fontFamily: "var(--serif)", fontWeight: 300, fontStyle: "italic",
          fontSize: "1.2rem", lineHeight: 1.5, color: "var(--text-muted)",
          maxWidth: 420, marginBottom: "2.5rem",
        }}>
          In the meantime — you might notice yourself already paying closer attention
          to the story you're living. That's the process beginning.
        </p>
      </Fade>

      <Fade delay={0.42}>
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem",
        }}>
          <p style={{
            fontFamily: "var(--sans)", fontWeight: 300, fontSize: "0.92rem",
            color: "var(--text-secondary)", marginBottom: "0.3rem",
          }}>
            You can learn more about where Loore is headed.
          </p>
          <Link to="/vision" style={{
            fontFamily: "var(--sans)", fontWeight: 300, fontSize: "0.88rem",
            color: "var(--accent)", textDecoration: "none",
            borderBottom: "1px solid var(--accent-glow)",
            paddingBottom: 2,
          }}>
            Read the vision &rarr;
          </Link>
        </div>
      </Fade>

      <Fade delay={0.48}>
        <WaitlistDeleteAccount user={user} />
      </Fade>
    </div>
  );
}

// "Delete my account" for a waitlisted account (#269): every account can
// delete itself, and this is the only page an unapproved account reaches.
// The same dialog and request as the Account page.
function WaitlistDeleteAccount({ user }) {
  const [open, setOpen] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const info = user.account_deletion;
  if (!info) return null;

  const requestDeletion = async (typed) => {
    const res = await api.post("/account/delete", { confirm: typed });
    setOpen(false);
    if (res.data.status === "scheduled") {
      // Signed out on the server: a full load drops the app's state.
      window.location.assign(
        `/account-deleted?on=${encodeURIComponent(res.data.delete_on || "")}`);
      return;
    }
    setLinkSent(true);
  };

  const days = info.grace_days || 30;
  const minutes = Math.round((info.link_expires_in || 3600) / 60);
  return (
    <div style={{ maxWidth: 420, marginTop: "3rem" }}>
      <p style={deleteTextStyle}>
        {linkSent
          ? `Check your email: Loore sent a confirmation link to ${user.email}. `
            + "Nothing changes until you open it and confirm there. "
            + `The link works for ${minutes} minutes.`
          : "Deletes your account at once and signs you out everywhere. If "
            + "you change your mind, you can still restore it by signing in "
            + `within ${days} days; after that it is deleted forever, with `
            + "everything in it."}
      </p>
      {!linkSent && (info.refusal ? (
        <p style={deleteTextStyle}>{info.refusal.message}</p>
      ) : (
        <button type="button" onClick={() => setOpen(true)} style={deleteButtonStyle}>
          Delete my account…
        </button>
      ))}
      <DeleteAccountDialog
        open={open}
        username={user.username}
        email={user.email}
        info={info}
        xConnected={!!user.data_deletion?.x_connected}
        writingDeletionAt={user.data_deletion?.status === "scheduled" ? user.data_deletion.purge_at : null}
        onConfirm={requestDeletion}
        onClose={close}
      />
    </div>
  );
}

const deleteTextStyle = {
  fontFamily: "var(--sans)", fontWeight: 300, fontSize: "0.82rem",
  lineHeight: 1.6, color: "var(--text-muted)", marginBottom: "0.8rem",
};

const deleteButtonStyle = {
  padding: "8px 16px", borderRadius: "6px", border: "1px solid var(--border)",
  background: "none", color: "var(--error)", cursor: "pointer",
  fontFamily: "var(--sans)", fontWeight: 300, fontSize: "0.85rem",
};

// The answer to a request never says whether the address already belongs to
// another account (that would let anyone test addresses), so such a request
// looks like any other and its mail never comes. This is the only hint.
const PENDING_HINT = "Nothing after a few minutes? Check the spelling. An "
  + "address that already signs in to Loore can't be added here.";

const pendingActionStyle = (disabled) => ({
  background: "none", border: "none", padding: 0,
  color: "var(--accent)", cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.6 : 1,
  fontFamily: "var(--sans)", fontWeight: 300, fontSize: "0.88rem",
});
